import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MoreHorizontal, 
  Heart, 
  ListPlus, 
  Plus,
  Share2,
  User,
  ExternalLink,
  Check,
  Music2,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLibrary } from '@/hooks/useLibrary';
import { useUserPlaylists, useCreatePlaylist, useAddTrackToPlaylist, type Playlist } from '@/hooks/usePlaylists';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

interface TrackMenuProps {
  track: MusicTrack;
  children?: React.ReactNode;
  className?: string;
  showHeart?: boolean;
}

export function TrackMenu({ track, children, className, showHeart = false }: TrackMenuProps) {
  const { user } = useCurrentUser();
  const { isLiked, toggleLike, isToggling } = useLibrary();
  const { data: playlists } = useUserPlaylists();
  const createPlaylist = useCreatePlaylist();
  const addToPlaylist = useAddTrackToPlaylist();
  const { toast } = useToast();

  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const liked = isLiked(track.id);
  const npub = nip19.npubEncode(track.event.pubkey);
  const trackAddress = `30999:${track.event.pubkey}:${track.event.tags.find(([n]) => n === 'd')?.[1] || track.id}`;

  const handleToggleLike = () => {
    if (!user) {
      toast({ title: 'Sign in to save tracks', variant: 'destructive' });
      return;
    }
    toggleLike(track.id);
  };

  const handleAddToPlaylist = async (playlist: Playlist) => {
    try {
      await addToPlaylist.mutateAsync({ playlist, trackAddress });
      toast({ title: `Added to ${playlist.title}` });
    } catch (error) {
      if ((error as Error).message === 'Track already in playlist') {
        toast({ title: 'Already in playlist', variant: 'destructive' });
      } else {
        toast({ title: 'Failed to add to playlist', variant: 'destructive' });
      }
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    
    setIsCreating(true);
    try {
      await createPlaylist.mutateAsync({
        title: newPlaylistName.trim(),
        trackAddresses: [trackAddress],
      });
      toast({ title: `Created "${newPlaylistName}" with this track` });
      setShowCreatePlaylist(false);
      setNewPlaylistName('');
    } catch {
      toast({ title: 'Failed to create playlist', variant: 'destructive' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleShare = async () => {
    // Use query parameter so it works on any deployment
    const url = `${window.location.origin}?track=${encodeURIComponent(track.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied!' });
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {children || (
            <button 
              className={cn(
                'p-1.5 rounded-full transition-all',
                'text-muted-foreground hover:text-foreground',
                'hover:bg-white/10',
                className
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="end" 
          className="w-56 glass border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Like */}
          <DropdownMenuItem 
            onClick={handleToggleLike}
            disabled={isToggling}
            className="cursor-pointer"
          >
            <Heart className={cn(
              'h-4 w-4 mr-3',
              liked && 'fill-red-500 text-red-500'
            )} />
            {liked ? 'Remove from Library' : 'Save to Library'}
            {isToggling && <Loader2 className="h-3 w-3 ml-auto animate-spin" />}
          </DropdownMenuItem>

          {/* Add to Playlist */}
          {user && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <ListPlus className="h-4 w-4 mr-3" />
                Add to Playlist
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48 glass border-white/10">
                <DropdownMenuItem 
                  onClick={() => setShowCreatePlaylist(true)}
                  className="cursor-pointer"
                >
                  <Plus className="h-4 w-4 mr-3" />
                  Create Playlist
                </DropdownMenuItem>
                
                {playlists && playlists.length > 0 && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {playlists.map((playlist) => {
                      const isInPlaylist = playlist.trackAddresses.includes(trackAddress);
                      return (
                        <DropdownMenuItem
                          key={playlist.id}
                          onClick={() => !isInPlaylist && handleAddToPlaylist(playlist)}
                          disabled={isInPlaylist}
                          className="cursor-pointer"
                        >
                          <Music2 className="h-4 w-4 mr-3 text-muted-foreground" />
                          <span className="truncate flex-1">{playlist.title}</span>
                          {isInPlaylist && <Check className="h-3 w-3 ml-2 text-primary" />}
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          <DropdownMenuSeparator className="bg-white/10" />

          {/* Go to Artist */}
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link to={`/${npub}`}>
              <User className="h-4 w-4 mr-3" />
              Go to Artist
            </Link>
          </DropdownMenuItem>

          {/* Share */}
          <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
            <Share2 className="h-4 w-4 mr-3" />
            Share
          </DropdownMenuItem>

          {/* Open in new tab */}
          <DropdownMenuItem asChild className="cursor-pointer">
            <a href={track.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-3" />
              Open Audio File
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Playlist Dialog */}
      <Dialog open={showCreatePlaylist} onOpenChange={setShowCreatePlaylist}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle>Create Playlist</DialogTitle>
            <DialogDescription>
              Create a new playlist with this track
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="playlist-name">Playlist Name</Label>
            <Input
              id="playlist-name"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              placeholder="My Playlist"
              className="mt-2"
              onKeyDown={(e) => e.key === 'Enter' && handleCreatePlaylist()}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreatePlaylist(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePlaylist} disabled={isCreating || !newPlaylistName.trim()}>
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Standalone heart button
export function LikeButton({ track, className }: { track: MusicTrack; className?: string }) {
  const { user } = useCurrentUser();
  const { isLiked, toggleLike, isToggling } = useLibrary();
  const { toast } = useToast();

  const liked = isLiked(track.id);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast({ title: 'Sign in to save tracks', variant: 'destructive' });
      return;
    }
    toggleLike(track.id);
  };

  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      onClick={handleClick}
      disabled={isToggling}
      className={cn(
        'p-1.5 rounded-full transition-all',
        liked 
          ? 'text-red-500' 
          : 'text-muted-foreground hover:text-red-400',
        'hover:bg-white/10',
        className
      )}
    >
      <Heart className={cn('h-4 w-4', liked && 'fill-current')} />
    </motion.button>
  );
}
